/* eslint-disable max-lines */
import { useEffect, useMemo, useCallback, useRef } from "react";
import { useWatch } from "react-hook-form";
import type { ID } from "common/utils/models";
import {
  useCompositionsList,
  useVariants,
  useCompositionForm
} from "@design-lab/entities/composition";
import { quantityChangedAtom } from "@design-lab/entities/composition/model";
import { useSetAtom } from "jotai";
import { trackQuantityChanged } from "@design-lab/shared/lib/analytics";
import { useDesignLabConfig, useIsSample } from "@design-lab/shared/lib/config";
import { DEFAULT_SAMPLE_SIZE } from "@design-lab/shared/constants";
import type { InventoryError } from "@design-lab/shared/types";
import { SizeItem } from "./SizeItem";
import type { BasePrice } from "./calculateBasePrice";
import { SampleSizeSelector } from "./SampleSizeSelector";

type InventoryData = {
  [variantId: string]: number;
};

type FastestDeliveryStock =
  | {
      enabled: boolean;
      availableCompositionIds: number[];
      variantStockCounts: Record<string, number>;
      variantTotalStockCounts: Record<string, number>;
    }
  | undefined;

type SizeSelectorProps = {
  inventoryData: InventoryData | undefined;
  isLoading: boolean;
  compositionId: ID;
  basePrice: BasePrice | undefined;
  getVariantPrice: (variantId: string) => string | undefined;
  fastestDeliveryStock?: FastestDeliveryStock;
};

export function SizeSelector(props: SizeSelectorProps) {
  const {
    inventoryData,
    getVariantPrice,
    isLoading,
    compositionId,
    basePrice,
    fastestDeliveryStock
  } = props;
  const { form } = useCompositionForm();
  const { compositions, formCompositions } = useCompositionsList();
  const { getVariantQuantity } = useVariants();
  const isSample = useIsSample();
  const bumpQuantityChanged = useSetAtom(quantityChangedAtom);
  const composition = compositions?.find(c => c.id === compositionId);
  const config = useDesignLabConfig();
  const madeSamplesV1 = config.flip?.madeSamplesV1;

  const inventoryErrors = useWatch({
    control: form.control,
    name: "inventoryErrors",
    defaultValue: []
  }) as InventoryError[];

  const preferredSampleVariant = useMemo(() => {
    if (!composition || !isSample || !composition.variants || composition.variants.length === 0)
      return null;
    return (
      composition.variants.find(v => v.size === DEFAULT_SAMPLE_SIZE) || composition.variants[0]
    );
  }, [composition, isSample]);

  // Check if current composition is a "Fast Delivery" color
  const isFastDeliveryColor = useMemo(() => {
    if (!fastestDeliveryStock?.enabled || !fastestDeliveryStock?.availableCompositionIds) {
      return false;
    }
    const compositionIdNum = parseInt(String(compositionId), 10);
    return fastestDeliveryStock.availableCompositionIds.includes(compositionIdNum);
  }, [fastestDeliveryStock, compositionId]);

  // Get optimal DC stock for a variant
  const getOptimalDcStock = useCallback(
    (variantId: ID): number | undefined => {
      if (!isFastDeliveryColor || !fastestDeliveryStock?.variantStockCounts) {
        return undefined;
      }
      return fastestDeliveryStock.variantStockCounts[String(variantId)];
    },
    [isFastDeliveryColor, fastestDeliveryStock]
  );

  useEffect(() => {
    if (!composition || !preferredSampleVariant || !isSample || madeSamplesV1) return;

    const hasExistingSelection = composition.variants.some(
      v => getVariantQuantity(compositionId, v.id) > 0
    );

    if (hasExistingSelection) return;

    if (inventoryData && inventoryData[preferredSampleVariant.id] === 0) return;

    const compositionIndex = formCompositions?.findIndex(c => c.id === compositionId) ?? -1;
    if (compositionIndex >= 0) {
      form.setValue(
        `addedCompositions.${compositionIndex}.variants.${preferredSampleVariant.id}`,
        1
      );
      form.trigger(`addedCompositions.${compositionIndex}`);
    }
  }, [
    composition,
    preferredSampleVariant,
    compositionId,
    getVariantQuantity,
    form,
    formCompositions,
    isSample,
    madeSamplesV1,
    inventoryData
  ]);

  const displayVariants = useMemo(() => {
    if (!composition) return [];
    if (!isSample) return composition.variants || [];
    return preferredSampleVariant ? [preferredSampleVariant] : [];
  }, [composition, isSample, preferredSampleVariant]);

  // Debounced form trigger to avoid excessive validation calls
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedTrigger = useCallback(
    (compositionIndex: number) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        form.trigger(`addedCompositions.${compositionIndex}`);
      }, 300);
    },
    [form]
  );

  useEffect(
    () => () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    },
    []
  );

  if (!composition || !composition.variants) return null;

  const { variants } = composition;

  const changeQuantity = (variantId: ID, quantity: number) => {
    const compositionIndex = formCompositions?.findIndex(c => c.id === compositionId) ?? -1;
    if (compositionIndex < 0) return;

    const formComposition = formCompositions[compositionIndex];
    const variant = variants.find(v => v.id === variantId);

    if (formComposition && variant) {
      trackQuantityChanged({
        catalogId: formComposition.catalogId,
        colorName: formComposition.colorName,
        size: variant.size,
        quantity
      });
    }
    form.setValue(`addedCompositions.${compositionIndex}.variants.${variantId}`, quantity);
    const currentErrors = form.getValues("inventoryErrors") || [];
    const filteredErrors = currentErrors.filter(
      error => !(error.compositionId === compositionId && error.variantId === variantId)
    );
    form.setValue("inventoryErrors", filteredErrors);

    // Signal quantity changed so delivery date sync can check for boundary crossing
    bumpQuantityChanged(prev => prev + 1);

    debouncedTrigger(compositionIndex);
  };

  const handleIncrement = (variantId: ID) => {
    // In sample mode, L size is fixed at quantity 1
    if (isSample) return;

    const currentQuantity = getVariantQuantity(compositionId, variantId) || 0;
    changeQuantity(variantId, currentQuantity + 1);
  };

  const handleDecrement = (variantId: ID) => {
    // In sample mode, L size is fixed at quantity 1
    if (isSample) return;

    const currentQuantity = getVariantQuantity(compositionId, variantId) || 0;
    if (currentQuantity > 0) {
      changeQuantity(variantId, currentQuantity - 1);
    }
  };

  const handleInputChange = (variantId: ID, value: string) => {
    // In sample mode, L size is fixed at quantity 1
    if (isSample) return;

    const parsedValue = parseInt(value, 10);
    if (!Number.isNaN(parsedValue) && parsedValue >= 0) {
      changeQuantity(variantId, parsedValue);
    } else if (value === "") {
      changeQuantity(variantId, 0);
    }
  };

  const handleSampleSizeChange = (selectedVariantId: ID) => {
    // Set all variants to 0 first, then set selected to 1
    variants.forEach(variant => {
      const quantity = variant.id === selectedVariantId ? 1 : 0;
      changeQuantity(variant.id, quantity);
    });
  };

  const getInventoryCount = (variantId: ID): string => {
    if (isLoading || !inventoryData) return "...";
    const count = inventoryData[variantId];
    return count !== undefined ? count.toString() : "...";
  };

  const getSelectedSampleVariantId = (): ID | "" => {
    const selectedVariant = variants.find(variant => {
      const quantity = getVariantQuantity(compositionId, variant.id) || 0;
      return quantity > 0;
    });
    return selectedVariant?.id || "";
  };

  const isOutOfStock = (variantId: ID): boolean => {
    if (!inventoryData) return false;
    return inventoryData[variantId] === 0;
  };

  const getVariantInventoryError = (variantId: ID): InventoryError | undefined =>
    inventoryErrors?.find(
      error => error.compositionId === compositionId && error.variantId === variantId
    );

  if (isSample) {
    return (
      <SampleSizeSelector
        compositionId={compositionId}
        variants={variants}
        selectedVariantId={getSelectedSampleVariantId()}
        onSampleSizeChange={handleSampleSizeChange}
        isOutOfStock={isOutOfStock}
      />
    );
  }

  return (
    <div className="flex flex-wrap justify-start gap-2" data-testid="size-selector-grid">
      {displayVariants.map(({ id, size: variantSize }) => {
        const variantId = id;
        const outOfStock = isOutOfStock(variantId);
        const inventoryError = getVariantInventoryError(variantId);
        const quantity =
          isSample && !madeSamplesV1 ? 1 : getVariantQuantity(compositionId, variantId) || 0;

        return (
          <SizeItem
            key={variantId}
            variantId={variantId}
            variantSize={variantSize}
            quantity={quantity}
            isOutOfStock={outOfStock}
            inventoryCount={getInventoryCount(variantId)}
            {...(inventoryError && { inventoryError })}
            basePrice={basePrice}
            getVariantPrice={getVariantPrice}
            onIncrement={handleIncrement}
            onDecrement={handleDecrement}
            onInputChange={handleInputChange}
            isSample={isSample}
            optimalDcStock={getOptimalDcStock(variantId)}
            isFastDeliveryColor={isFastDeliveryColor}
          />
        );
      })}
    </div>
  );
}
